import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSpec } from '../../../contexts/SpecContext';

interface VulnStats {
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

export const useVulnerabilityData = () => {
  const navigate = useNavigate();
  const { activeSpec } = useSpec();
  const [vulnerabilities, setVulnerabilities] = useState<any[]>([]);
  const [filteredVulnerabilities, setFilteredVulnerabilities] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [falsePositivesOnly, setFalsePositivesOnly] = useState<boolean>(false);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(10);

  const [sortField, setSortField] = useState<string>('foundDate');
  const [sortDirection, setSortDirection] = useState<string>('desc');

  const [stats, setStats] = useState<VulnStats>({
    total: 0,
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
  });

  const hasActiveFilters = !!(severityFilter || statusFilter || falsePositivesOnly || searchTerm);

  const calculateStats = useCallback((vulns: any[]): void => {
    setStats({
      total: vulns.length,
      critical: vulns.filter((v) => v.severity?.toLowerCase() === 'critical').length,
      high: vulns.filter((v) => v.severity?.toLowerCase() === 'high').length,
      medium: vulns.filter((v) => v.severity?.toLowerCase() === 'medium').length,
      low: vulns.filter((v) => v.severity?.toLowerCase() === 'low').length,
    });
  }, []);

  const loadVulnerabilities = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${import.meta.env.VITE_API_URL}/api/vulnerabilities`;
      if (activeSpec?.id) {
        url += `?schema_id=${activeSpec.id}`;
      }

      const response = await fetch(url);
      if (response.ok) {
        const result = await response.json();
        const vulns = result.data || result.vulnerabilities || [];
        setVulnerabilities(vulns);
        calculateStats(vulns);
      } else {
        toast.error('Failed to load vulnerabilities');
      }
    } catch (error) {
      console.error('Error loading vulnerabilities:', error);
      toast.error('Failed to load vulnerabilities');
    } finally {
      setLoading(false);
    }
  }, [activeSpec, calculateStats]);

  const applyFilters = useCallback(() => {
    let filtered = [...vulnerabilities];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (v) =>
          v.title?.toLowerCase().includes(term) ||
          v.id?.toLowerCase().includes(term) ||
          v.endpoint?.toLowerCase().includes(term) ||
          v.type?.toLowerCase().includes(term),
      );
    }

    if (severityFilter) {
      filtered = filtered.filter((v) => v.severity?.toLowerCase() === severityFilter);
    }

    if (statusFilter) {
      filtered = filtered.filter((v) => v.status?.toLowerCase() === statusFilter);
    }

    if (falsePositivesOnly) {
      filtered = filtered.filter((v) => v.is_false_positive === 1);
    }

    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === 'severity') {
        const severityOrder: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1, info: 0 };
        aVal = severityOrder[a.severity?.toLowerCase()] || 0;
        bVal = severityOrder[b.severity?.toLowerCase()] || 0;
      } else if (sortField === 'foundDate') {
        aVal = new Date(a.foundDate);
        bVal = new Date(b.foundDate);
      }

      return sortDirection === 'asc' ? (aVal > bVal ? 1 : -1) : aVal < bVal ? 1 : -1;
    });

    setFilteredVulnerabilities(filtered);
  }, [vulnerabilities, severityFilter, statusFilter, falsePositivesOnly, searchTerm, sortField, sortDirection]);

  useEffect(() => {
    loadVulnerabilities();
  }, [loadVulnerabilities]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleSort = (field: string): void => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedVulnerabilities = filteredVulnerabilities.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredVulnerabilities.length / itemsPerPage);

  const handleSelectItem = (id: string): void => {
    setSelectedItems((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const handleSelectAll = (): void => {
    if (selectedItems.length === paginatedVulnerabilities.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(paginatedVulnerabilities.map((v) => v.id));
    }
  };

  const handleBulkFalsePositive = async (): Promise<void> => {
    if (selectedItems.length === 0) {
      toast.warning('No vulnerabilities selected');
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/vulnerabilities/batch-false-positive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vulnerability_ids: selectedItems,
          reason: 'Marked as false positive from vulnerability management',
        }),
      });

      if (response.ok) {
        toast.success(`Marked ${selectedItems.length} vulnerabilities as false positive`);
        setSelectedItems([]);
        loadVulnerabilities();
      } else {
        toast.error('Failed to update vulnerabilities');
      }
    } catch (error) {
      console.error('Error updating vulnerabilities:', error);
      toast.error('Failed to update vulnerabilities');
    }
  };

  const resetFilters = (): void => {
    setSeverityFilter('');
    setStatusFilter('');
    setFalsePositivesOnly(false);
    setSearchTerm('');
    setCurrentPage(1);
  };

  const openDetailPage = (vulnerability: any): void => {
    navigate(`/vulnerabilities/${vulnerability.id}`);
  };

  return {
    navigate,
    loading,
    stats,
    searchTerm,
    severityFilter,
    statusFilter,
    falsePositivesOnly,
    hasActiveFilters,
    filteredVulnerabilities,
    paginatedVulnerabilities,
    selectedItems,
    sortField,
    sortDirection,
    currentPage,
    totalPages,
    indexOfFirstItem,
    indexOfLastItem,
    setSearchTerm,
    setSeverityFilter,
    setStatusFilter,
    setFalsePositivesOnly,
    setCurrentPage,
    handleSort,
    handleSelectItem,
    handleSelectAll,
    handleBulkFalsePositive,
    resetFilters,
    openDetailPage,
  };
};
